import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/DefaultLayout';
import api from '../../utils/api';
import { BookOpen, BrainCircuit, Activity, Database, ShieldCheck, CheckCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#4f46e5', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f43f5e', '#84cc16'];

const MetricCard = ({ title, value, previousValue, icon: Icon, colorClass }) => {
    const delta = previousValue !== undefined ? value - previousValue : 0;
    const isPositive = delta > 0;
    const isNegative = delta < 0;
    const deltaText = isPositive ? `+${delta}` : (isNegative ? `${delta}` : '0');

    return (
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 relative overflow-hidden group hover:border-gray-300 transition-colors">
            <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 blur-2xl ${colorClass}`}></div>

            <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                    <Icon className="text-gray-600" size={24} />
                </div>
                <div className="flex flex-col items-end">
                    <span className={`text-sm font-bold px-2 py-1 rounded-full bg-gray-50 ${isPositive ? 'text-green-600' : (isNegative ? 'text-red-600' : 'text-gray-500')}`}>
                        {deltaText}
                    </span>
                    <span className="text-xs text-gray-400 mt-1">vs previous</span>
                </div>
            </div>

            <div className="flex flex-col">
                <span className="text-4xl font-black tracking-tight text-gray-900 mb-2">{value !== undefined ? value.toLocaleString() : '-'}</span>
                <span className="text-gray-500 font-medium text-sm tracking-wide uppercase">{title}</span>
            </div>
        </div>
    );
};

const CustomPieChart = ({ title, data }) => {
    const chartData = data ? Object.entries(data).map(([name, value]) => ({ name, value })) : [];

    return (
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 h-96 flex flex-col">
            <h3 className="text-lg font-bold text-gray-800 mb-4">{title}</h3>
            <div className="flex-grow">
                {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={5}
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => value.toLocaleString()} />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">No data available</div>
                )}
            </div>
        </div>
    );
};

const CustomBarChart = ({ title, data }) => {
    const chartData = data ? Object.entries(data).map(([name, value]) => ({ name: name || 'Unspecified', count: value })).sort((a, b) => b.count - a.count) : [];

    return (
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 h-96 flex flex-col">
            <h3 className="text-lg font-bold text-gray-800 mb-4">{title}</h3>
            <div className="flex-grow">
                {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 40 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 12, fill: '#6b7280' }} />
                            <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                            <Tooltip cursor={{ fill: '#f3f4f6' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                            <Bar dataKey="count" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">No data available</div>
                )}
            </div>
        </div>
    );
};

const SubjectChapterHeatmap = ({ title, data }) => {
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('');

    useEffect(() => {
        if (data && Object.keys(data).length > 0 && !selectedClass) {
            setSelectedClass(Object.keys(data)[0]);
        }
    }, [data, selectedClass]);

    useEffect(() => {
        if (selectedClass && data && data[selectedClass]) {
            const subjects = Object.keys(data[selectedClass]);
            if (subjects.length > 0 && !selectedSubject) {
                setSelectedSubject(subjects[0]);
            } else if (!subjects.includes(selectedSubject)) {
                setSelectedSubject(subjects.length > 0 ? subjects[0] : '');
            }
        }
    }, [selectedClass, data, selectedSubject]);

    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 h-[400px] flex flex-col">
                <h3 className="text-lg font-bold text-gray-800 mb-4">{title}</h3>
                <div className="flex items-center justify-center h-full text-gray-400">No data available</div>
            </div>
        );
    }

    const availableClasses = Object.keys(data);
    const classData = selectedClass && data[selectedClass] ? data[selectedClass] : {};
    const availableSubjects = Object.keys(classData);
    const chapterData = selectedSubject && classData[selectedSubject] ? classData[selectedSubject] : {};

    const chapters = Object.entries(chapterData).map(([name, count]) => ({ name: name || 'Unknown', count })).sort((a, b) => b.count - a.count);
    const maxCount = chapters.length > 0 ? Math.max(...chapters.map(c => c.count)) : 1;

    // Helper to determine background intensity
    const getHeatmapColor = (count, max) => {
        const ratio = count / max;
        if (ratio === 0) return 'bg-gray-100 text-gray-400';
        if (ratio < 0.2) return 'bg-indigo-100 text-indigo-800';
        if (ratio < 0.4) return 'bg-indigo-300 text-indigo-900';
        if (ratio < 0.6) return 'bg-indigo-500 text-white';
        if (ratio < 0.8) return 'bg-indigo-700 text-white';
        return 'bg-indigo-900 text-white';
    };

    return (
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 min-h-[400px] flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-800">{title}</h3>
                <div className="flex space-x-3">
                    <select
                        className="border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                        value={selectedClass}
                        onChange={(e) => setSelectedClass(e.target.value)}
                    >
                        {availableClasses.map(cls => (
                            <option key={cls} value={cls}>{cls}</option>
                        ))}
                    </select>

                    <select
                        className="border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                        value={selectedSubject}
                        onChange={(e) => setSelectedSubject(e.target.value)}
                        disabled={availableSubjects.length === 0}
                    >
                        {availableSubjects.map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                        ))}
                        {availableSubjects.length === 0 && <option value="">No Subjects</option>}
                    </select>
                </div>
            </div>

            <div className="flex-grow">
                {chapters.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {chapters.map((chapter) => (
                            <div
                                key={chapter.name}
                                className={`rounded-xl p-4 flex flex-col justify-between min-h-[7rem] transition-transform hover:scale-105 shadow-sm border border-gray-50 ${getHeatmapColor(chapter.count, maxCount)}`}
                                title={`${chapter.name}: ${chapter.count} questions`}
                            >
                                <span className="font-semibold text-xs leading-snug line-clamp-3 mb-2">{chapter.name}</span>
                                <span className="text-2xl font-black self-end mt-auto">{chapter.count}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full min-h-[200px] text-gray-400">
                        No chapters available for {selectedSubject || 'this selection'}
                    </div>
                )}
            </div>
        </div>
    );
};

const SimpleHeatmap = ({ title, data }) => {
    const [searchQuery, setSearchQuery] = useState('');

    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 min-h-[300px] flex flex-col">
                <h3 className="text-lg font-bold text-gray-800 mb-4">{title}</h3>
                <div className="flex items-center justify-center h-full text-gray-400">No data available</div>
            </div>
        );
    }

    const allItems = Object.entries(data).map(([name, count]) => ({ name: name || 'Unknown', count })).sort((a, b) => b.count - a.count);

    const items = allItems.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const maxCount = allItems.length > 0 ? Math.max(...allItems.map(c => c.count)) : 1;

    // Helper to determine background intensity
    const getHeatmapColor = (count, max) => {
        const ratio = count / max;
        if (ratio === 0) return 'bg-gray-100 text-gray-400';
        if (ratio < 0.2) return 'bg-purple-100 text-purple-800';
        if (ratio < 0.4) return 'bg-purple-300 text-purple-900';
        if (ratio < 0.6) return 'bg-purple-500 text-white';
        if (ratio < 0.8) return 'bg-purple-700 text-white';
        return 'bg-purple-900 text-white';
    };

    return (
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 min-h-[300px] flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-800">{title}</h3>
                <div className="relative w-64">
                    <input
                        type="text"
                        placeholder="Search keywords..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-sm font-medium focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                    />
                    <div className="absolute left-3 top-2.5 text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="flex-grow">
                {items.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {items.map((item) => (
                            <div
                                key={item.name}
                                className={`rounded-xl p-4 flex flex-col justify-between min-h-[7rem] transition-transform hover:scale-105 shadow-sm border border-gray-50 ${getHeatmapColor(item.count, maxCount)}`}
                                title={`${item.name}: ${item.count} items`}
                            >
                                <span className="font-semibold text-xs leading-snug line-clamp-3 mb-2">{item.name}</span>
                                <span className="text-2xl font-black self-end mt-auto">{item.count}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full min-h-[150px] text-gray-400">
                        No chapters match "{searchQuery}"
                    </div>
                )}
            </div>
        </div>
    );
};

let globalMetricsCache = null;

const AdminDashboard = () => {
    const [metrics, setMetrics] = useState(globalMetricsCache);
    const [isLoading, setIsLoading] = useState(!globalMetricsCache);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (globalMetricsCache) {
            setMetrics(globalMetricsCache);
            setIsLoading(false);
            return;
        }

        const fetchMetrics = async () => {
            try {
                const response = await api.get('/dashboard/metrics');
                globalMetricsCache = response.data;
                setMetrics(response.data);
            } catch (err) {
                console.error("Failed to fetch dashboard metrics", err);
                setError("Failed to load dashboard data. Please check backend connection.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchMetrics();
    }, []);

    if (isLoading) {
        return (
            <Layout>
                <div className="flex flex-col items-center justify-center h-full min-h-[60vh]">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
                    <p className="mt-6 text-indigo-600 font-semibold text-xl tracking-widest uppercase">Initializing Telemetry...</p>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="flex flex-col items-center justify-center h-full min-h-[60vh] p-8">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-lg text-center text-red-800">
                        <Activity size={48} className="mx-auto mb-4 text-red-500" />
                        <h2 className="text-2xl font-bold mb-2">Telemetry Error</h2>
                        <p>{error}</p>
                    </div>
                </div>
            </Layout>
        );
    }

    const { current, previous } = metrics || {};

    return (
        <Layout>
            <div className="bg-gray-50 font-sans pb-16 pt-8">
                <div className="max-w-[1400px] mx-auto px-6">

                    {/* SECTION: QUESTION TELEMETRY */}
                    <div className="mb-16">
                        <div className="flex items-center space-x-3 mb-6">
                            <Database className="text-indigo-600" size={28} />
                            <h2 className="text-2xl font-bold text-gray-900">Question Telemetry</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                            <MetricCard
                                title="Total Questions"
                                value={current?.totalQuestions}
                                previousValue={previous?.totalQuestions}
                                icon={Database}
                                colorClass="bg-blue-500"
                            />
                            <MetricCard
                                title="Added Last 7 Days"
                                value={current?.questionsAddedLastWeek}
                                previousValue={previous?.questionsAddedLastWeek}
                                icon={Activity}
                                colorClass="bg-emerald-500"
                            />
                            <MetricCard
                                title="Active Questions"
                                value={current?.activeQuestions}
                                previousValue={previous?.activeQuestions}
                                icon={CheckCircle}
                                colorClass="bg-green-500"
                            />
                            <MetricCard
                                title="Copyrighted Content (%)"
                                value={current?.copyrightedPercentage}
                                previousValue={previous?.copyrightedPercentage}
                                icon={ShieldCheck}
                                colorClass="bg-amber-500"
                            />
                        </div>

                        {/* Question Charts */}
                        <div className="grid grid-cols-1 mb-8">
                            <SubjectChapterHeatmap
                                title="Questions by Subject & Chapter (Heatmap)"
                                data={current?.questionsPerClassSubjectAndChapter}
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <CustomBarChart
                                title="Questions by Academic Class"
                                data={current?.questionsPerClassStandard}
                            />
                            <CustomPieChart
                                title="Questions by Format Type"
                                data={current?.questionsPerType}
                            />
                            <CustomPieChart
                                title="Questions by Difficulty"
                                data={current?.questionsPerDifficulty}
                            />
                        </div>
                    </div>

                    <hr className="border-gray-200 mb-16" />

                    {/* SECTION: CONCEPT TELEMETRY */}
                    <div className="mb-8">
                        <div className="flex items-center space-x-3 mb-6">
                            <BrainCircuit className="text-purple-600" size={28} />
                            <h2 className="text-2xl font-bold text-gray-900">Concept Telemetry</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <MetricCard
                                title="Total Concepts"
                                value={current?.totalConcepts}
                                previousValue={previous?.totalConcepts}
                                icon={BrainCircuit}
                                colorClass="bg-purple-500"
                            />
                            <MetricCard
                                title="Concepts Added Last Week"
                                value={current?.conceptsAddedLastWeek}
                                previousValue={previous?.conceptsAddedLastWeek}
                                icon={Activity}
                                colorClass="bg-fuchsia-500"
                            />
                            <MetricCard
                                title="Concept Chapters"
                                value={current?.conceptsPerChapter ? Object.keys(current.conceptsPerChapter).length : 0}
                                previousValue={previous?.conceptsPerChapter ? Object.keys(previous.conceptsPerChapter).length : 0}
                                icon={BookOpen}
                                colorClass="bg-indigo-500"
                            />
                        </div>

                        <div className="grid grid-cols-1 mb-6">
                            <SimpleHeatmap
                                title="Concepts by Chapter Density"
                                data={current?.conceptsPerChapter}
                            />
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <CustomPieChart
                                title="Concept Items by Skill Level"
                                data={current?.conceptItemsPerSkillLevel}
                            />
                        </div>
                    </div>

                </div>
            </div>
        </Layout>
    );
};

export default AdminDashboard;
